import * as React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Upload as KendoUpload } from "@progress/kendo-react-upload";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import Papa from "papaparse";

import ErrorBanner from "../../components/ErrorBanner";
import { useQueryGetClientsMasterList } from "../../data/services/clientAdminService";
import { useMutatePostFeatureAccessClients } from "../../data/services/featureAccessService";

const ACTIONS = {
  ADD: "Add",
  ALREADY_ADDED: "Already Added",
  INVALID: "Invalid",
};

// Feature keys are stored and compared lowercase; see FeatureKeys.Normalize on
// the server. Normalizing here too means the preview shows exactly what will be
// written, and "FeatureA"/"featurea" in one file collapse to one row.
const normalizeFeatureKey = (raw) =>
  raw === undefined || raw === null ? "" : String(raw).trim().toLowerCase();

// Matches the server-side AddFeatureAccessControlClients.FeatureKeyPattern.
const FEATURE_KEY_PATTERN = /^[a-z0-9_.-]{1,100}$/;

// Numeric-only IDs are zero-padded to 5 chars to match the workflow-wide
// convention (see pages/workflow/Panels/OEWorkflowImportClientsModel.jsx).
const normalizeId = (raw) => {
  if (raw === undefined || raw === null) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  return /^\d+$/.test(trimmed) ? trimmed.padStart(5, "0") : trimmed;
};

/**
 * CSV import for the Feature Access Control allow-list.
 *
 * Append/upsert only. Unlike the workflow client importer this does NOT sync:
 * clients already on the list but absent from the file are left alone, never
 * removed. Removal is an explicit, separate action.
 */
const FeatureAccessImportClientsModal = ({
  oePeriodId,
  featureKey,
  currentEntries,
  knownFeatureKeys,
  onClose,
  onSubmit,
}) => {
  const [files, setFiles] = React.useState([]);
  const [parseError, setParseError] = React.useState(null);
  const [fileRows, setFileRows] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);

  const { data: availableClients } = useQueryGetClientsMasterList();
  const addClientsHandler = useMutatePostFeatureAccessClients();

  // Keyed on clientId|featureKey, because a client may legitimately hold
  // several features and each is a separate row.
  const currentPairs = React.useMemo(() => {
    return new Set(
      (currentEntries || []).map(
        (entry) =>
          `${normalizeId(entry.clientId)}|${normalizeFeatureKey(entry.featureKey)}`
      )
    );
  }, [currentEntries]);

  const definedInCode = React.useMemo(() => {
    return new Set(
      (knownFeatureKeys || [])
        .filter((option) => option.isDefinedInCode)
        .map((option) => normalizeFeatureKey(option.featureKey))
    );
  }, [knownFeatureKeys]);

  const validClientsById = React.useMemo(() => {
    const map = new Map();
    (availableClients?.data || []).forEach((client) => {
      map.set(normalizeId(client.clientId), client);
    });
    return map;
  }, [availableClients]);

  const previewRows = React.useMemo(() => {
    const seen = new Set();
    const rows = [];

    for (const fileRow of fileRows) {
      const id = fileRow.clientId;
      // A blank FeatureKey cell falls back to the feature selected on the page,
      // so single-feature files need no FeatureKey column at all.
      const rowFeatureKey = fileRow.featureKey || normalizeFeatureKey(featureKey);

      const pairKey = `${id}|${rowFeatureKey}`;
      if (!id || seen.has(pairKey)) continue;
      seen.add(pairKey);

      const validClient = validClientsById.get(id);
      const featureKeyIsValid =
        rowFeatureKey.length > 0 && FEATURE_KEY_PATTERN.test(rowFeatureKey);

      if (!validClient || !featureKeyIsValid) {
        rows.push({
          clientId: id,
          clientName: validClient ? validClient.clientName : "(unknown)",
          featureKey: rowFeatureKey,
          action: ACTIONS.INVALID,
          isNewFeature: false,
          invalidReason: !validClient
            ? "Client not in master list"
            : "Invalid feature key",
        });
      } else if (currentPairs.has(pairKey)) {
        rows.push({
          clientId: validClient.clientId,
          clientName: validClient.clientName,
          featureKey: rowFeatureKey,
          action: ACTIONS.ALREADY_ADDED,
          isNewFeature: false,
        });
      } else {
        rows.push({
          clientId: validClient.clientId,
          clientName: validClient.clientName,
          featureKey: rowFeatureKey,
          action: ACTIONS.ADD,
          isNewFeature: !definedInCode.has(rowFeatureKey),
        });
      }
    }

    return rows;
  }, [fileRows, validClientsById, currentPairs, definedInCode, featureKey]);

  const summary = React.useMemo(
    () => ({
      add: previewRows.filter((row) => row.action === ACTIONS.ADD),
      alreadyAdded: previewRows.filter(
        (row) => row.action === ACTIONS.ALREADY_ADDED
      ),
      invalid: previewRows.filter((row) => row.action === ACTIONS.INVALID),
      newFeature: previewRows.filter(
        (row) => row.action === ACTIONS.ADD && row.isNewFeature
      ),
    }),
    [previewRows]
  );

  const canApply = !submitting && !parseError && summary.add.length > 0;

  const onAddFileHandler = (event) => {
    setFiles(event.newState);
    setParseError(null);
    setFileRows([]);

    const rawFile = event.newState[0]?.getRawFile?.();
    if (!rawFile) return;

    Papa.parse(rawFile, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => (header || "").trim(),
      complete: (results) => {
        const headers = results.meta?.fields || [];
        const clientIdHeader = headers.find(
          (header) => header.toLowerCase() === "clientid"
        );
        // FeatureKey is optional: without it every row targets the feature
        // selected on the page.
        const featureKeyHeader = headers.find(
          (header) => header.toLowerCase() === "featurekey"
        );

        if (!clientIdHeader) {
          setParseError(
            "The file is missing a required 'ClientId' column header."
          );
          setFileRows([]);
          return;
        }

        const parsed = results.data
          .map((row) => ({
            clientId: normalizeId(row[clientIdHeader]),
            featureKey: featureKeyHeader
              ? normalizeFeatureKey(row[featureKeyHeader])
              : "",
          }))
          .filter((row) => row.clientId.length > 0);

        if (parsed.length === 0) {
          setParseError("The file does not contain any Client Ids.");
          setFileRows([]);
          return;
        }

        setFileRows(parsed);
      },
      error: () => {
        setParseError("Failed to parse the file. Please check the format.");
        setFileRows([]);
      },
    });
  };

  const onRemoveFileHandler = (event) => {
    setFiles(event.newState);
    setFileRows([]);
    setParseError(null);
  };

  const onDownloadTemplate = () => {
    const csvData = Papa.unparse([{ ClientId: "", FeatureKey: "" }]);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "Feature Access Clients Import Template.csv";
    link.click();
    // The object URL pins the blob in memory for the life of the document.
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      await addClientsHandler.mutateAsync({
        oePeriodId,
        entries: summary.add.map((row) => ({
          clientId: row.clientId,
          featureKey: row.featureKey,
        })),
      });
      setSubmitting(false);
      onSubmit();
    } catch {
      setSubmitting(false);
    }
  };

  const actionCell = (props) => {
    const action = props.dataItem.action;
    const color =
      action === ACTIONS.ADD
        ? "green"
        : action === ACTIONS.INVALID
        ? "#a94442"
        : "";
    return (
      <td
        style={{
          color,
          fontWeight: action === ACTIONS.INVALID ? "bold" : "",
        }}
        title={props.dataItem.invalidReason ?? ""}
      >
        {action}
        {props.dataItem.isNewFeature && " (new feature)"}
      </td>
    );
  };

  return (
    <Dialog title={"Import clients to feature"} onClose={onClose} width={700}>
      <ErrorBanner handler={addClientsHandler} />
      <p>
        Upload a CSV with a <b>ClientId</b> column and an optional{" "}
        <b>FeatureKey</b> column. Rows without a FeatureKey use the feature
        selected on the page (<b>{normalizeFeatureKey(featureKey) || "none"}</b>
        ). To give one client several features, list it once per feature.
      </p>
      <p>
        Clients on the file that are not yet on the list are added. Clients
        already on the list are left unchanged, and clients <b>not</b> on the
        file are never removed.
      </p>
      <p>
        <Button themeColor={"info"} fillMode="flat" onClick={onDownloadTemplate}>
          Download Template
        </Button>
      </p>
      <KendoUpload
        autoUpload={false}
        showActionButtons={false}
        files={files}
        multiple={false}
        onAdd={onAddFileHandler}
        onRemove={onRemoveFileHandler}
        restrictions={{ allowedExtensions: [".csv"] }}
      />
      {parseError && (
        <div
          style={{
            backgroundColor: "#f8d7da",
            padding: 10,
            marginTop: 10,
            borderRadius: ".25rem",
            color: "#721c24",
          }}
        >
          {parseError}
        </div>
      )}
      {previewRows.length > 0 && (
        <>
          <div style={{ marginTop: 20 }}>
            <b>{summary.add.length}</b> to add,{" "}
            <b>{summary.alreadyAdded.length}</b> already added,{" "}
            <b>{summary.invalid.length}</b> invalid.
          </div>
          {summary.newFeature.length > 0 && (
            <div
              data-testid="new-feature-warning"
              style={{
                backgroundColor: "#fff3cd",
                padding: 10,
                marginTop: 10,
                borderRadius: ".25rem",
                color: "#856404",
              }}
            >
              {summary.newFeature.length} row(s) reference a feature key that is
              not defined in code:{" "}
              <b>
                {[...new Set(summary.newFeature.map((row) => row.featureKey))].join(
                  ", "
                )}
              </b>
              . These will be saved, but gated screens will not read them until a
              developer adds the matching constant.
            </div>
          )}
          {summary.invalid.length > 0 && (
            <div
              style={{
                backgroundColor: "#fff3cd",
                padding: 10,
                marginTop: 10,
                borderRadius: ".25rem",
                color: "#856404",
              }}
            >
              {summary.invalid.length} row(s) are invalid and will be skipped
              (client id not in the master list, or an unusable feature key).
              Hover a row's Action cell for the reason.
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <Grid data={previewRows} style={{ maxHeight: "300px" }}>
              <Column field="clientId" title="Client Id" width={120} />
              <Column field="clientName" title="Client Name" />
              <Column field="featureKey" title="Feature" width={200} />
              <Column
                field="action"
                title="Action"
                width={160}
                cell={actionCell}
              />
            </Grid>
          </div>
        </>
      )}
      <div style={{ paddingTop: 20 }}></div>
      <DialogActionsBar layout={"end"}>
        <Button onClick={onClose}>{"Cancel"}</Button>
        <Button
          type={"submit"}
          themeColor={"success"}
          disabled={!canApply}
          onClick={handleSubmit}
        >
          {"Apply"}
          {submitting && <span className="k-icon k-i-loading"></span>}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default FeatureAccessImportClientsModal;
